import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Mail, X, BellRing, Siren, Wrench, RefreshCw } from 'lucide-react';
import { useLang } from '../i18n';
import api from '../api';

const ICONS = { renewal: RefreshCw, claim_status: Wrench, claim_decision: Siren };

export default function NotificationBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState(false);
  const ref = useRef(null);

  const loadCount = () => api.get('/notifications/unread-count').then((r) => setCount(r.data.count)).catch(() => {});
  const loadList = () => api.get('/notifications').then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    loadCount();
    api.get('/settings/notifications').then((r) => setEmail(r.data.notify_email || '')).catch(() => {});
    const iv = setInterval(loadCount, 20000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => { const n = !open; setOpen(n); if (n) loadList(); };

  const markAll = async () => { await api.post('/notifications/read-all'); await loadList(); await loadCount(); };
  const clickItem = async (n) => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); await loadList(); await loadCount(); } };

  const saveEmail = async () => {
    await api.post('/settings/notifications', { notify_email: email });
    setSaved(true); setTimeout(() => setSaved(false), 1800);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="chip px-2.5 py-1.5 text-slate-200 relative">
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-jade text-ink text-[10px] font-bold flex items-center justify-center">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-[340px] max-h-[70vh] overflow-hidden glass rounded-2xl border border-white/10 shadow-jade z-50 flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-white/8">
            <div className="flex items-center gap-2 text-sm font-semibold"><BellRing className="w-4 h-4 text-jade" /> {t('notifications')}</div>
            <div className="flex items-center gap-2">
              <button onClick={markAll} data-testid="notif-mark-all" className="text-[11px] font-mono text-slate-400 hover:text-jade flex items-center gap-1"><Check className="w-3 h-3" /> {t('mark_all_read')}</button>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="p-3 border-b border-white/8 bg-white/[0.02]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><Mail className="w-3 h-3" /> {t('notif_email_label')}</div>
            <div className="flex gap-2">
              <input data-testid="notif-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('notif_email_ph')}
                className="input flex-1 px-3 py-2 text-xs" />
              <button onClick={saveEmail} data-testid="notif-email-save" className="btn-jade px-3 py-2 text-xs">{saved ? t('saved') : t('save')}</button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500" data-testid="notif-empty">{t('notif_empty')}</div>}
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => clickItem(n)} data-testid={`notif-${n.id}`}
                  className={`w-full text-left px-3 py-3 border-b border-white/5 flex gap-3 hover:bg-white/[0.03] ${n.read ? 'opacity-60' : ''}`}>
                  <div className="shrink-0 mt-0.5"><Icon className="w-4 h-4 text-jade" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-jade shrink-0" />}
                      <span className="truncate">{n.title}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{n.body}</div>
                    <div className="text-[10px] font-mono text-slate-600 mt-1">
                      {n.created_at.slice(0, 16).replace('T', ' ')}{n.email_sent ? ' · emailed' : ''}
                    </div>
                    {n.type === 'renewal' && n.policy_id && (
                      <span role="button" data-testid={`notif-renew-${n.id}`}
                        onClick={(e) => { e.stopPropagation(); setOpen(false); navigate(`/renew/${n.policy_id}`); }}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-jade border border-jade/40 rounded-full px-2.5 py-1 hover:bg-jade/10">
                        <RefreshCw className="w-3 h-3" /> {t('renew_now')}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
